import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { hasItems, padNumber, takeFirst } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type GraphicCardsBlockViewProps<TCard = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  getItemKey?: (card: TCard, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  renderCard: (card: TCard, index: number, count: string) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function GraphicCardsBlockView<TCard = unknown>({
  anchor,
  cards = [],
  getItemKey,
  hasTopDivider = true,
  mode = "light",
  renderCard,
  sectionProps,
  theme = "white"
}: GraphicCardsBlockViewProps<TCard>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = cards ?? [];
  if (!hasItems(items)) {
    return null;
  }
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledGrid $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          },
          "@media (min-width: 1024px)": {
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-x-reverse": "0",
              "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
              "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
            },
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.background, themeStyle.border]}>
            {items.map((card, idx) => <_StyledDiv2 key={getItemKey?.(card, idx) || idx}>
                {renderCard(card, idx, padNumber(idx + 1))}
              </_StyledDiv2>)}
          </_StyledGrid>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-13yvs5e-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-13yvs5e-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-13yvs5e-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = /*#__PURE__*/_styled4("div").withConfig({
  displayName: "graphic-cards___StyledDiv2",
  componentId: "sc-gaqs07-0"
})({
  "gridColumn": "1 / -1",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 4 / span 4"
  }
});